/**
 * useDetailSurface — everything a detail page's shell needs, in one hook.
 *
 * A detail page kept four pieces of state that always changed together and
 * always in the same way: the pane LAYOUT, the one SELECTED row the shared
 * detail container edits, whatever the READER pane is showing, and the ACTIVE
 * TAB. They interlock — switching tabs drops the selection and closes the
 * detail pane; selecting a row opens and raises it; opening a document opens
 * and raises the reader — and every page that hand-rolled them got a
 * different subset of those rules right.
 *
 * So the hook owns the interlock, and the page keeps only what is genuinely
 * its own (the URL, the data, the tab LABELS).
 *
 * THE TAB VOCABULARY IS THE SURFACE'S. `defineDetailSurface({ tabs })` names
 * the tabs; this hook checks the URL's `?tab=` against them and ignores one
 * it does not recognise, so a stale link lands on the page's first tab rather
 * than a blank canvas. The page still owns the labels — they need `t()`, an
 * icon, sometimes a licence check.
 *
 * TWO ENTRY POINTS, because not every page has every third of the API:
 *
 *   useDetailSurface              panes + reader + the shared detail container
 *   useSurfaceWithoutSelection    panes (+ reader) for a page with no detail
 *                                 pane, so nothing carries dead selection
 *                                 typing
 *
 * It also runs the one-time migration off the surface's old unscoped storage
 * keys, BEFORE the layout is first read, so a returning user's arrangement is
 * found rather than replaced by the defaults.
 */

import { useCallback, useMemo, useState } from 'react';
import usePanelSurface, { UsePanelSurfaceResult } from './usePanelSurface';
import { DetailSurfaceDefinition } from './defineDetailSurface';
import {
  DetailSelection,
  DetailSurfaceApi,
  DetailSurfaceApiWithoutSelection,
} from './DetailSurfaceContext';
import useLayoutUserId from '../../../services/layout/useLayoutUserId';
import { migrateLegacyLayouts } from '../../../services/layout/layoutStore';

export interface UseDetailSurfaceOptions<Tab extends string = string> {
  /** The record this surface belongs to, for an entity-scoped surface. */
  entityId?: string;
  /**
   * Tab shown on arrival — usually the raw `?tab=` off the URL.
   *
   * Checked against the surface's declared tabs; anything else is ignored in
   * favour of the first declared tab, so the page never opens on nothing.
   */
  initialTab?: string;
  /**
   * Called when `goToTab` changes tabs, so the page can push the URL.
   * Not called for `setActiveTab`, which exists for syncing FROM the URL.
   */
  onTabChange?: (tab: Tab) => void;
}

/** The half of the result that every surface has. */
export interface UseSurfaceWithoutSelectionResult<
  Id extends string,
  Reader = never,
  Tab extends string = string,
> {
  /** The layout state — pass this to `DetailPageSurface` and `PaneRail`. */
  panels: UsePanelSurfaceResult<Id>;
  /** The small API the tabs read from context. */
  api: DetailSurfaceApiWithoutSelection<Id, Reader>;
  /** The declared tabs, in bar order — the page maps them to labels. */
  tabs: Tab[];
  activeTab: Tab | '';
  /** Set the tab WITHOUT the side effects — for syncing from the URL. */
  setActiveTab: (tab: string) => void;
  /** Change tabs: clears the selection, closes the detail pane, notifies. */
  goToTab: (tab: string) => void;
  readerContent: Reader | null;
  setReaderContent: (content: Reader | null) => void;
}

export interface UseDetailSurfaceResult<
  Id extends string,
  Kind extends string,
  Reader,
  Tab extends string = string,
> extends Omit<UseSurfaceWithoutSelectionResult<Id, Reader, Tab>, 'api'> {
  api: DetailSurfaceApi<Id, Kind, Reader>;
  selection: DetailSelection<Kind> | null;
}

/** Migrations are per user AND per surface; both are cheap to remember. */
const migrated = new Set<string>();

export function useDetailSurface<
  Id extends string,
  Kind extends string = string,
  Reader = unknown,
  Tab extends string = string,
>(
  definition: DetailSurfaceDefinition<Id, Tab>,
  { entityId, initialTab, onTabChange }: UseDetailSurfaceOptions<Tab> = {}
): UseDetailSurfaceResult<Id, Kind, Reader, Tab> {
  const userId = useLayoutUserId();

  /*
    The migration has to happen BEFORE the first read, and the first read is
    `usePanelSurface`'s `useState` initialiser — an effect would run after it
    and the user would see the defaults. A `useMemo` on (userId, definition)
    is the only hook that runs early enough, and the module-level guard keeps
    it to once per user per surface however many pages mount.
  */
  const surface = useMemo(() => {
    const guard = `${userId}::${definition.config.kind}:${definition.config.name}`;
    if (!migrated.has(guard)) {
      migrated.add(guard);
      migrateLegacyLayouts(userId, definition.legacyRules);
    }
    return definition.surfaceFor(userId, entityId);
  }, [definition, userId, entityId]);

  const panels = usePanelSurface(surface);

  // A `?tab=` from a stale link, a renamed tab or someone else's URL is not
  // state: it resolves to the surface's first declared tab instead.
  const [activeTab, setActiveTabState] = useState<Tab | ''>(
    () => definition.resolveTab(initialTab)
  );
  const [selection, setSelection] = useState<DetailSelection<Kind> | null>(null);
  const [readerContent, setReaderContent] = useState<Reader | null>(null);

  const { detailPane, readerPane } = definition.config;

  /* Syncing FROM the URL goes through the same check — the URL is the one
     input nobody controls. */
  const setActiveTab = useCallback(
    (tab: string) => setActiveTabState(definition.resolveTab(tab)),
    [definition]
  );

  const goToTab = useCallback((tab: string) => {
    if (!definition.isTab(tab)) return;
    setActiveTabState(tab);
    // A stale editor left open on a row the new tab does not show is worse
    // than no editor: it looks like it belongs to what you are now reading.
    setSelection(null);
    if (detailPane) panels.setVisible(detailPane, false);
    onTabChange?.(tab);
  }, [definition, detailPane, panels, onTabChange]);

  const api: DetailSurfaceApi<Id, Kind, Reader> = useMemo(() => ({
    openPane: (id: Id) => {
      panels.setVisible(id, true);
      panels.focus(id);
    },
    closePane: (id: Id) => panels.setVisible(id, false),
    isPaneOpen: (id: Id) => panels.layout.slots[id]?.visible ?? false,
    openReader: (content: Reader) => {
      /* No reader pane means nowhere to put it. The types stop this at the
         call site; ignoring it here keeps a stray runtime call from opening
         a pane that does not exist. */
      if (!readerPane) return;
      setReaderContent(content);
      panels.setVisible(readerPane, true);
      panels.focus(readerPane);
    },
    goToTab,

    selection,
    select: (next: DetailSelection<Kind>) => {
      setSelection(next);
      if (detailPane) {
        panels.setVisible(detailPane, true);
        panels.focus(detailPane);
      }
    },
    clearSelection: () => {
      setSelection(null);
      if (detailPane) panels.setVisible(detailPane, false);
    },
  }), [panels, goToTab, selection, detailPane, readerPane]);

  return {
    panels,
    api,
    tabs: definition.tabs,
    activeTab,
    setActiveTab,
    goToTab,
    selection,
    readerContent,
    setReaderContent,
  };
}

/**
 * The same surface, for a page with NO detail pane.
 *
 * Same state, same interlock — the difference is the TYPE: the selection
 * third of the API is absent, so a page that never selects anything neither
 * names a selection `Kind` it does not have nor offers call sites a `select`
 * that would open a pane it never declared.
 */
export function useSurfaceWithoutSelection<
  Id extends string,
  Reader = never,
  Tab extends string = string,
>(
  definition: DetailSurfaceDefinition<Id, Tab>,
  options: UseDetailSurfaceOptions<Tab> = {}
): UseSurfaceWithoutSelectionResult<Id, Reader, Tab> {
  const { panels, api, tabs, activeTab, setActiveTab, goToTab, readerContent, setReaderContent } =
    useDetailSurface<Id, never, Reader, Tab>(definition, options);

  return { panels, api, tabs, activeTab, setActiveTab, goToTab, readerContent, setReaderContent };
}

export default useDetailSurface;
